import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { RoutePaths } from '../../constants/routes';
import styles from './Header.module.scss';
import { MobileLogoImage } from 'assets/images';
import cx from 'classnames';
import { ActivityFeed, Button, Icon, RolePermissionChecker, Text } from 'components';
import { useCreatePatient, useDevice, useOutsideClick } from 'hooks';
import { selectAppModals } from 'store/app/selectors';
import { appActions } from 'store/app/slice';
import { selectCurrentUser } from 'store/auth/selectors';
import { authActions } from 'store/auth/slice';
import { selectActivityFeedFilter } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { UserRole } from 'types';

type Props = {
  toggleMobileMenu(): void;
  isMobileMenuOpen: boolean;
};

export const Header = ({ toggleMobileMenu, isMobileMenuOpen }: Props) => {
  const showNotificationsButtonRef = useRef<HTMLDivElement | null>() as React.MutableRefObject<HTMLDivElement>;
  const currentUser = useSelector(selectCurrentUser);
  const { pathname } = useLocation();
  const modals = useSelector(selectAppModals);
  const { isTablet } = useDevice();
  const currentUserRole: string = currentUser?.role || '';

  const currentUserName: string = currentUser?.name || '';
  const [name, surname] = currentUserName.split(' ');
  const initials = (name?.[0] ?? '') + (surname ? surname?.[0] : '');

  const activityFeedFilter = useSelector(selectActivityFeedFilter);

  const dispatch = useDispatch();

  const { openModalHandler } = useCreatePatient();

  const handleCreateUserClick = () => {
    dispatch(appActions.setIsModalOpened({ name: 'createUser', isOpened: true }));
  };

  const onOpenCreatePatientModalHandler = () => {
    openModalHandler();
  };

  const showNotifications = (show: boolean) => {
    dispatch(appActions.setIsModalOpened({ name: 'activityFeed', isOpened: show }));
  };

  const notificationsRef = useOutsideClick<HTMLDivElement>((event) => {
    !showNotificationsButtonRef?.current?.contains(event?.target as HTMLElement) && showNotifications(false);
  });

  useEffect(() => {
    dispatch(authActions.getCurrentUser());

    const updateData = () => {
      if (document.visibilityState === 'visible') {
        dispatch(authActions.getCurrentUser());
        dispatch(patientsActions.getActivityFeed());
      }
    };

    window.addEventListener('visibilitychange', updateData);

    return () => window.removeEventListener('visibilitychange', updateData);
  }, [dispatch]);

  useEffect(() => {
    dispatch(patientsActions.getActivityFeed());
  }, [dispatch, activityFeedFilter]);

  const currentUserRoleText = currentUserRole === UserRole.CLINIC ? 'Studio' : currentUserRole;

  return (
    <header className={styles.container}>
      <div className={styles.leftPart}>
        <div className={styles.userPic}>
          <Text type="button">{initials}</Text>
        </div>
        <div className={styles.userInfo}>
          <Text type="button" className={styles.userInfoName}>
            {currentUserName}
          </Text>
          <Text type="caption" className={styles.userInfoRole}>
            {currentUserRoleText.toLowerCase()}
          </Text>
        </div>
        <div
          className={cx(styles.iconContainer, {
            [styles.active]: modals.activityFeed,
          })}>
          <div ref={showNotificationsButtonRef}>
            <Icon name="Bell" onClick={() => showNotifications(!modals.activityFeed)} />
          </div>
          {modals.activityFeed && (
            <div className={styles.notificationsWrapper} ref={!isTablet ? notificationsRef : null}>
              <ActivityFeed onClose={() => showNotifications(false)} />
            </div>
          )}
        </div>
      </div>
      <img src={MobileLogoImage} alt="logo-mobile" className={styles.logoMobile} />
      <button
        className={cx(styles.menuButton, { [styles.mobileMenuOpen]: isMobileMenuOpen })}
        onClick={toggleMobileMenu}>
        <span className={styles.menuLine} />
        <span className={styles.menuLine} />
      </button>
      {pathname === RoutePaths.BASE_PATH && (
        <RolePermissionChecker rolesAllowedToView={[UserRole.ADMIN]}>
          <Button onClick={handleCreateUserClick} leftIcon="Plus" className={styles.headerButton}>
            Create user
          </Button>
        </RolePermissionChecker>
      )}
      {(pathname.includes(RoutePaths.PATIENTS) || pathname === RoutePaths.DASHBOARD) && (
        <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC]}>
          <Button onClick={onOpenCreatePatientModalHandler} leftIcon="Plus" className={styles.headerButton}>
            Create patient
          </Button>
        </RolePermissionChecker>
      )}
    </header>
  );
};
